import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getLocalizedPath, getRouteByKey } from "@/utils/routing";
import { getAvailableLanguagesForDomain, handleLanguageSwitch, type Language } from "@/utils/languageSwitching";
import { useRotatingCTA } from "@/hooks/useRotatingCTA";
import { appLegalUrl } from "@/utils/appLegal";
import PeppolTrustBadge from "@/components/PeppolTrustBadge";
import MobileAppLinks from "@/components/MobileAppLinks";
import FooterCtaBand from "@/components/footer/FooterCtaBand";
import FooterLinkGroup from "@/components/footer/FooterLinkGroup";
import { useFooterLinks } from "@/components/footer/useFooterLinks";
import { Facebook, Twitter, Linkedin, Mail, Phone, MapPin, FlaskConical, BookOpen } from "lucide-react";
import { formatCopyrightYears } from "@/utils/copyright";
import { useURL } from "@/contexts/URLContext";

interface FooterProps {
  /** Hide the "ready to try Bidmio?" band on pages that already end with FinalCTA. */
  showCta?: boolean;
}

const iconTile =
  "w-11 h-11 bg-background/10 rounded-lg flex items-center justify-center hover:bg-background/20 transition-colors duration-200";
// Company column + one column per rendered link group (EN/DE have no comparisons).
// Full class names so Tailwind keeps them.
const desktopColumns: Record<number, string> = {
  3: "lg:grid-cols-[1.5fr_repeat(3,minmax(0,1fr))]",
  4: "lg:grid-cols-[1.5fr_repeat(4,minmax(0,1fr))]",
  5: "lg:grid-cols-[1.5fr_repeat(5,minmax(0,1fr))]",
};
const contactButton =
  "flex min-h-11 flex-1 items-center justify-center gap-2 rounded-lg bg-background/10 hover:bg-background/20 transition-colors md:min-h-0 md:flex-none md:justify-start md:bg-transparent md:p-0 md:text-background/70 md:hover:bg-transparent md:hover:text-background";

const Footer = ({ showCta = true }: FooterProps) => {
  const { t, i18n } = useTranslation();
  const { getBookDemoUrl, handleBookDemoClick } = useRotatingCTA(false);
  const lang = i18n.language as 'sk' | 'cs' | 'da' | 'en' | 'de';
  const groups = useFooterLinks({ href: getBookDemoUrl(), onClick: handleBookDemoClick('footer') });
  // On phones every group is collapsed until tapped; one open at a time.
  const [openGroup, setOpenGroup] = useState<string | null>(null);

  // Map language to logo
  const logoMap: Record<string, string> = {
    sk: '/images/bidmio-logo-sk-white.png',
    cs: '/images/bidmio-logo-cs-white.png',
    da: '/images/bidmio-logo-da-white.png',
    en: '/images/bidmio-logo-en-white.png',
    de: '/images/bidmio-logo-de-white.png',
  };
  const currentLogo = logoMap[i18n.language] || '/images/bidmio-logo-sk-white.png';

  const { fullUrl } = useURL();
  const currentHostname = fullUrl ? new URL(fullUrl).hostname.toLowerCase() : '';
  const availableLanguages = getAvailableLanguagesForDomain(currentHostname, i18n.language as Language);
  const handleLanguageChange = (langCode: string) => {
    handleLanguageSwitch(langCode as Language);
  };

  const email = t('layout.footer.contact.email');
  const phone = t('layout.footer.contact.phone');

  // Canonical contracts live on app.bidmio.{tld} — do not open marketing copies.
  // Listed once, in the bottom bar.
  const legalLinks = [
    { title: t('layout.footer.legal.terms'), href: appLegalUrl(lang, '/terms'), testId: 'footer-bottom-terms' },
    { title: t('layout.footer.legal.privacy'), href: appLegalUrl(lang, '/privacy'), testId: 'footer-bottom-privacy' },
    { title: t('layout.footer.legal.dpa'), href: appLegalUrl(lang, '/dpa'), testId: 'footer-bottom-dpa' },
    { title: t('layout.footer.legal.security'), href: appLegalUrl(lang, '/security'), testId: 'footer-bottom-security' },
    { title: t('layout.footer.legal.status'), href: getRouteByKey('status', lang), testId: 'footer-bottom-status' },
    { title: t('layout.footer.legal.cookies'), href: appLegalUrl(lang, '/cookies'), testId: 'footer-bottom-cookies' },
  ];

  const socialLinks = [
    { icon: Facebook, href: "https://www.facebook.com/bidmio", label: "Facebook" },
    { icon: Twitter, href: "https://twitter.com/bidmio", label: "Twitter" },
    { icon: Linkedin, href: "https://www.linkedin.com/company/bidmio", label: "LinkedIn" }
  ];


  return (
    <footer className="bg-foreground text-background" data-testid="site-footer">
      <div className="container-custom pt-10 md:pt-16">
        {showCta && <FooterCtaBand />}

        <div className={`grid gap-y-8 md:grid-cols-3 md:gap-x-8 md:gap-y-10 ${desktopColumns[groups.length] ?? desktopColumns[5]}`}>
          {/* Company info */}
          <div className="space-y-4 md:col-span-3 lg:col-span-1">
            <Link to={getLocalizedPath("/", lang)} className="inline-block" aria-label={t("layout.footer.quickLinks.home")}>
              <picture>
                <source type="image/webp" srcSet={currentLogo.replace(/\.png$/, ".webp")} />
                <img src={currentLogo} alt={t("layout.header.logoAlt")} className="h-12 w-auto" />
              </picture>
            </Link>
            <p className="text-background/70 leading-relaxed">{t('layout.footer.company.description')}</p>
            <div className="flex gap-3 text-sm md:flex-col md:gap-2">
              <a href={`tel:${phone.replace(/\s+/g, '')}`} className={contactButton} data-testid="footer-contact-phone">
                <Phone className="w-4 h-4" aria-hidden="true" />
                <span className="md:hidden">{t('layout.footer.contact.call')}</span>
                <span className="hidden md:inline">{phone}</span>
              </a>
              <a href={`mailto:${email}`} className={contactButton} data-testid="footer-contact-email">
                <Mail className="w-4 h-4" aria-hidden="true" />
                <span className="md:hidden">{t('layout.footer.contact.write')}</span>
                <span className="hidden md:inline">{email}</span>
              </a>
              <span className="hidden items-center gap-2 text-background/70 md:flex">
                <MapPin className="w-4 h-4" aria-hidden="true" />
                {t('layout.footer.contact.location')}
              </span>
            </div>
            <div className="hidden pt-2 lg:block">
              <PeppolTrustBadge tone="dark" />
            </div>
          </div>

          {/* Link groups: collapsible on phones, columns from md up */}
          <div className="border-t border-background/15 md:contents md:border-0">
            {groups.map((group) => (
              <FooterLinkGroup
                key={group.id}
                group={group}
                open={openGroup === group.id}
                onToggle={() => setOpenGroup((current) => (current === group.id ? null : group.id))}
              />
            ))}
          </div>
        </div>

        <div className="mt-8 lg:hidden">
          <PeppolTrustBadge tone="dark" />
        </div>

        {/* Mobile app: App Store, Google Play and the web app in one band */}
        <MobileAppLinks className="mt-10" />

        {/* Bottom bar: legal (once), copyright, language, social */}
        <div className="mt-10 flex flex-col gap-6 border-t border-background/20 py-6 lg:flex-row lg:items-center lg:justify-between">
          <div className="space-y-2 text-sm text-background/70">
            <div className="flex flex-wrap gap-x-5 gap-y-1">
              {legalLinks.map((link) => (
                <a
                  key={link.testId}
                  href={link.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="py-1 hover:text-background transition-colors"
                  data-testid={link.testId}
                >
                  {link.title}
                </a>
              ))}
              {lang === 'de' && (
                <a href="/impressum/" className="py-1 hover:text-background transition-colors" data-testid="footer-bottom-impressum">
                  Impressum
                </a>
              )}
              <Link to={getRouteByKey('cookies', lang)} className="py-1 hover:text-background transition-colors" data-testid="footer-bottom-cookie-settings">
                {t('layout.footer.legal.cookieSettings')}
              </Link>
            </div>
            <p>
              {t('layout.footer.copyright', { year: formatCopyrightYears() })} · {t('layout.footer.company.legalEntity')}
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <select
              value={i18n.language}
              onChange={event => handleLanguageChange(event.target.value)}
              className="h-11 min-w-[8rem] flex-1 rounded-md border bg-foreground border-background/20 px-3 text-sm text-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring lg:w-40 lg:flex-none"
              aria-label={t('layout.footer.languageLabel')}
              data-testid="footer-language"
            >
              {availableLanguages.map(option => (
                <option key={option.code} value={option.code}>
                  {option.name}
                </option>
              ))}
            </select>
            {socialLinks.map((social) => {
              const IconComponent = social.icon;
              return (
                <a
                  key={social.label}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={social.label}
                  className={`${iconTile} shrink-0`}
                >
                  <IconComponent className="w-5 h-5" />
                </a>
              );
            })}
            {/* Dev-only shortcuts to admin dashboards — never rendered in production builds */}
            {import.meta.env.DEV && (
              <>
                <Link
                  to="/admin/videos/"
                  aria-label="Správa videí (dev)"
                  title="Správa ukážkových videí — len vo vývoji"
                  data-testid="link-footer-admin-videos"
                  className={`${iconTile} shrink-0`}
                >
                  <FlaskConical className="w-5 h-5" />
                </Link>
                <Link
                  to="/admin/articles/"
                  aria-label="Správa článkov (dev)"
                  title="Správa blogových článkov — len vo vývoji"
                  data-testid="link-footer-admin-articles"
                  className={`${iconTile} shrink-0`}
                >
                  <BookOpen className="w-5 h-5" />
                </Link>
              </>
            )}
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
